import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'sonner'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'
import { Loader2, Save } from 'lucide-react'
import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import { useLanguage } from '@/context/LanguageContext'
import { seedProfiles } from '@/lib/profileUtils'
import { blankLinkTarget, mapLinkTargetFromRaw } from '@/lib/linkTarget'
import {
  DraggableFieldArray,
  reorderFieldArray,
} from '@/components/shared/DraggableFieldArray'
import { NavigationItemEditor } from './components/NavigationItemEditor'
import {
  GET_NAVIGATION_SETTINGS,
  UPDATE_NAVIGATION_SETTINGS,
} from './navigationSettingsQueries'
import {
  buildNavigationSettingsFormSchema,
  buildNavigationVariables,
} from './navigationSettingsPayload'
import {
  NavItemFormValues,
  NavigationSettingsFormValues,
} from './types'

function createBlankNavItem(allLanguages: string[]): NavItemFormValues {
  return {
    _key: crypto.randomUUID(),
    type: 'link',
    ...blankLinkTarget(),
    panel_root_category_id: null,
    panel_root_page_id: null,
    panel_source: 'categories',
    profiles: seedProfiles(undefined, allLanguages, lang => ({
      language: lang,
      label: '',
    })),
    featured_page_id: null,
    links: [],
  }
}

function itemTitle(
  item: NavItemFormValues,
  index: number,
  languageId: number
): string {
  const label =
    item.profiles[languageId]?.label?.trim() ||
    item.profiles.find(p => p.label?.trim())?.label
  const typeLabel =
    item.type === 'mega_menu' ? 'Mega menu' : item.type === 'dropdown' ? 'Dropdown' : 'Link'
  return label?.trim() ? label : `${typeLabel} #${index + 1}`
}

export default function NavigationSettings() {
  const { allLanguages, languageId } = useLanguage()
  const activeLanguageId = Math.max(0, languageId)

  const { data, loading } = useQuery(GET_NAVIGATION_SETTINGS)
  const [updateNavigationSettings, { loading: saving }] = useMutation(
    UPDATE_NAVIGATION_SETTINGS,
    {
      refetchQueries: ['GET_NAVIGATION_SETTINGS'],
      onError: () => toast.error('Failed to save navigation settings'),
    }
  )

  // Recomputed whenever the active language changes so the schema's
  // "needs a label" issue attaches to the ONE profile index the form
  // actually renders a field for — see `buildNavigationSettingsFormSchema`'s
  // doc. react-hook-form re-reads `resolver` off its own options on every
  // render, so a fresh resolver here takes effect immediately.
  const resolver = useMemo(
    () => zodResolver(buildNavigationSettingsFormSchema(activeLanguageId)),
    [activeLanguageId]
  )

  const form = useForm<NavigationSettingsFormValues>({
    resolver,
    mode: 'onSubmit',
    defaultValues: { items: [] },
  })

  // Gated on `allLanguages.length > 0` — `LanguageContext.allLanguages`
  // starts empty and fills in asynchronously after its own query, and
  // `languageId` is `allLanguages.indexOf(language)` (`-1` until then).
  // Seeding before that would produce zero profile rows and field paths
  // like `profiles.-1.label`.
  useEffect(() => {
    if (loading || allLanguages.length === 0) return

    const rawItems: any[] = data?.findFirstShopSetting?.navigation_items ?? []
    const items: NavItemFormValues[] = rawItems.map(raw => ({
      _key: crypto.randomUUID(),
      type: raw.type,
      ...mapLinkTargetFromRaw(raw),
      panel_root_category_id: raw.panel_root_category_id ?? null,
      panel_root_page_id: raw.panel_root_page_id ?? null,
      // Persisted, not derived: a `manual` panel nulls BOTH `panel_root_*`
      // ids, so the old "whichever id is set" derivation could not tell it
      // apart from a category-sourced panel. Rows written before the column
      // existed were backfilled by the `navigation_panel_source` migration;
      // the `?? 'categories'` covers a partially-deployed backend.
      panel_source: raw.panel_source ?? 'categories',
      profiles: seedProfiles(
        raw.navigation_item_profiles,
        allLanguages,
        lang => ({
          language: lang,
          label: '',
        })
      ),
      featured_page_id: raw.featured_page_id ?? null,
      // Raw field is literally `profiles` (not `navigation_item_link_profiles`)
      // — see `GET_NAVIGATION_SETTINGS`'s doc comment.
      links: (raw.links ?? []).map((rawLink: any) => ({
        _key: crypto.randomUUID(),
        ...mapLinkTargetFromRaw(rawLink),
        profiles: seedProfiles(rawLink.profiles, allLanguages, lang => ({
          language: lang,
          label: '',
        })),
      })),
    }))

    form.reset({ items })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, allLanguages.length])

  // ONE `DragDropContext` for the whole page — react-beautiful-dnd doesn't
  // support nesting contexts, so the items list and each dropdown item's own
  // links list are siblings under this single context, routed by
  // `droppableId` — mirrors `FooterSettings`'s columns/links routing.
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination || source.droppableId !== destination.droppableId) return

    if (source.droppableId === 'nav-items') {
      const current = form.getValues('items') ?? []
      form.setValue(
        'items',
        reorderFieldArray(current, source.index, destination.index),
        { shouldDirty: true }
      )
      return
    }

    // Both a `dropdown` item's list and a manual `mega_menu` panel's list
    // live at `items.${n}.links`; they only differ by droppable prefix so two
    // lists can never share an id under the page's single DragDropContext.
    const match = /^nav-(?:dropdown|mega)-links-(\d+)$/.exec(source.droppableId)
    if (match) {
      // A dynamically-built path can't be narrowed to RHF's literal path
      // union — same dynamic-path convention as `FooterSettings`'s
      // `columns.${match[1]}.links`.
      const path = `items.${match[1]}.links` as any
      const current = form.getValues(path) ?? []
      form.setValue(path, reorderFieldArray(current, source.index, destination.index), {
        shouldDirty: true,
      })
    }
  }

  const onSubmit = async (values: NavigationSettingsFormValues) => {
    try {
      await updateNavigationSettings({
        variables: buildNavigationVariables(values),
      })
      toast.success('Navigation settings saved')
    } catch (error) {
      console.error('Save navigation settings error:', error)
    }
  }

  // `form.handleSubmit(onSubmit)` silently no-ops on invalid input —
  // `onSubmit` never runs and neither does the mutation's own `onError`, so
  // without this the Save button would fail with zero feedback whenever an
  // item's label/target validation fails. A collapsed row's fields aren't
  // mounted (see `DraggableFieldArray`), so an inline `FormMessage` alone
  // can't be relied on to surface the problem either.
  const onInvalid = () => {
    toast.error('Fix the highlighted navigation items before saving')
  }

  const showLoading = loading || allLanguages.length === 0

  if (showLoading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
          <div className="flex justify-between items-center mb-8">
            <HeaderSection
              title="Navigation"
              subtitle="Manage the B2B portal's navbar mega menu and links"
            />
            <Button type="submit" disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>

          <div className="max-w-[1000px]">
            <DragDropContext onDragEnd={handleDragEnd}>
              <DraggableFieldArray
                form={form}
                name="items"
                droppableId="nav-items"
                onAdd={() => createBlankNavItem(allLanguages)}
                itemTitle={(item, index) =>
                  itemTitle(item, index, activeLanguageId)
                }
                renderItem={index => (
                  <NavigationItemEditor
                    form={form}
                    index={index}
                    languageId={activeLanguageId}
                    allLanguages={allLanguages}
                  />
                )}
                addLabel="Add navigation item"
                emptyText="No navigation items yet."
              />
            </DragDropContext>
          </div>
        </form>
      </Form>
    </PageContent>
  )
}
